import {
    forwardRef,
    memo,
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState
} from "react";

import type {
    Attachment,
    AttachmentOrFileType,
    AttachmentOrNativeFile,
    AttachmentWithFileOrNativeFile,
    FileType,
    NativeImageType
} from "../../models/attachment";
import type {OptionType} from "../Select";
import UploadImageArea from "../UploadImageArea";
import {
    filterAttachmentOrFileType,
    filterAttachmentOrNativeImage,
    isAttachment,
    isFileType,
    isNativeImage
} from "../UploadImageArea/util";

type Props = {
    maxImages: number;
    tags?: OptionType<string>[];
    size?: "sm" | "md" | "lg";
    onDeleteImages: (keys: string[], id: string) => Promise<void>;
    onError?: (type: "maxImages") => void;
} & StrictUnion<
    | {
          isNative?: false;
          attachments?: AttachmentOrFileType[];
          canReorder?: boolean;
          onUploadImages: (
              objectID: string,
              file: FileType,
              onProgress?: (file: FileType, value: number) => void
          ) => Promise<Attachment>;
          onChange?: (files: AttachmentOrFileType[]) => void;
      }
    | {
          isNative: true;
          attachments?: AttachmentOrNativeFile[];
          onUploadImages: (
              objectID: string,
              file: NativeImageType,
              onProgress?: (file: NativeImageType, value: number) => void
          ) => Promise<Attachment>;
          onChange?: (files: AttachmentOrNativeFile[]) => void;
      }
>;

type FormUploadFileRef = {
    save: (objectID: string) => Promise<Attachment[]>;
};

const FormUploadFile = forwardRef<FormUploadFileRef, Props>(function FormUploadFile(
    {
        isNative,
        attachments,
        tags,
        maxImages,
        canReorder,
        size = "md",
        onChange,
        onUploadImages,
        onDeleteImages,
        onError
    },
    ref
) {
    const [uploading, setUploading] = useState(false);
    const deletingAttachmentsRef = useRef(new Set<string>());
    const attachmentOrFileListRef = useRef<AttachmentWithFileOrNativeFile[]>(attachments || []);
    const uploadImageRef = useRef<React.ComponentRef<typeof UploadImageArea>>(null);

    useLayoutEffect(() => {
        attachmentOrFileListRef.current = attachments ?? [];
    }, [attachments]);

    const handleSave = useCallback(
        async (objectID: string): Promise<Attachment[]> => {
            // delete selected attachments
            if (deletingAttachmentsRef.current.size) {
                setUploading(true);
                await onDeleteImages([...deletingAttachmentsRef.current], objectID);
            }

            const attachmentOrFiles = attachmentOrFileListRef.current;
            if (attachmentOrFiles.length) {
                setUploading(true);
                const filesUploaded = await Promise.all(
                    attachmentOrFiles.reduce((arr, file) => {
                        if (isFileType(file)) {
                            if (file.resize && !isNative) {
                                arr.push(
                                    onUploadImages(
                                        objectID,
                                        file,
                                        uploadImageRef.current?.onUploadProcess
                                    ).then(res => ({...res, tags: file.tags ?? []}))
                                );
                            }
                        } else if (isNativeImage(file)) {
                            if (isNative) {
                                arr.push(
                                    onUploadImages(
                                        objectID,
                                        file,
                                        uploadImageRef.current?.onUploadProcess
                                    ).then(res => ({...res, tags: file.tags ?? []}))
                                );
                            }
                        } else {
                            arr.push(Promise.resolve(file));
                        }
                        return arr;
                    }, [] as Promise<Attachment>[])
                );
                setUploading(false);
                uploadImageRef.current?.setAttachmentOrFileList(filesUploaded);
                return filesUploaded;
            }
            return Promise.resolve([]);
        },
        [onDeleteImages, onUploadImages, isNative]
    );

    useImperativeHandle(
        ref,
        () => {
            return {
                save: handleSave
            };
        },
        [handleSave]
    );

    const handleDeleteAttachment = useCallback(
        (attachment: Attachment) => {
            if (attachment.publicId) {
                deletingAttachmentsRef.current.add(attachment.publicId);
            }
            attachmentOrFileListRef.current = attachmentOrFileListRef.current.filter(
                item => !isAttachment(item) || item.url !== attachment.url
            );
            if (isNative) {
                onChange?.(filterAttachmentOrNativeImage(attachmentOrFileListRef.current));
            } else {
                onChange?.(filterAttachmentOrFileType(attachmentOrFileListRef.current));
            }
        },
        [onChange, isNative]
    );

    const handleChangeAttachmentOrNativeFileList = useCallback(
        (attachmentOrFileList: AttachmentOrNativeFile[]) => {
            attachmentOrFileListRef.current = attachmentOrFileList;
            if (isNative) {
                onChange?.(attachmentOrFileList);
            }
        },
        [onChange, isNative]
    );

    const handleChangeAttachmentOrFileTypeList = useCallback(
        (attachmentOrFileList: AttachmentOrFileType[]) => {
            attachmentOrFileListRef.current = attachmentOrFileList;
            if (!isNative) {
                onChange?.(attachmentOrFileList);
            }
        },
        [onChange, isNative]
    );

    if (isNative) {
        return (
            <UploadImageArea
                ref={uploadImageRef}
                isNative
                attachments={attachments}
                maxImages={maxImages}
                size={size}
                tagOptions={tags}
                uploading={uploading}
                onChangeAttachmentOrFileList={handleChangeAttachmentOrNativeFileList}
                onDeleteAttachment={handleDeleteAttachment}
                onError={onError}
            />
        );
    }

    return (
        <UploadImageArea
            ref={uploadImageRef}
            attachments={attachments}
            canReorder={canReorder && maxImages > 1}
            maxImages={maxImages}
            size={size}
            tagOptions={tags}
            uploading={uploading}
            onChangeAttachmentOrFileList={handleChangeAttachmentOrFileTypeList}
            onDeleteAttachment={handleDeleteAttachment}
            onError={onError}
        />
    );
});

export default memo(FormUploadFile);
